import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BrowserOAuthClient } from '@atproto/oauth-client-browser';
import { Agent } from '@atproto/api';
import clientMetadata from '../generated/client-metadata';
import { appBasename } from '../lib/paths.js';
import { getPdsEndpoint } from '../lib/xrpc.js';
import { checkDeployment, resetLocalSignInState } from '../lib/deployment.js';

export const AuthContext = createContext(null);

/**
 * The OAuth client starts from client metadata BUNDLED at build time
 * (src/generated/client-metadata.js, written by scripts/gen-client-metadata.mjs
 * from the same source as public/client-metadata.json). There is deliberately
 * NO fetch of client-metadata.json at startup: the PDS fetches that public
 * copy itself when it validates the client, so the browser never needs to —
 * and a network failure can never take the app down with "Failed to fetch".
 * The build gate (npm run check:scope) fails if the two copies drift.
 *
 * Rebuilt 2026-11-04 on the sifter base (a working OAuth SPA) + the follower
 * feature layer + the atprotozoa single-host / granular-scope doctrine.
 */
export const AuthProvider = ({ children }) => {
  const [client, setClient] = useState(null);
  const [session, setSession] = useState(null);
  const [agent, setAgent] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The redirect URI must be the one registered for the origin the user is on;
  // the OAuth client otherwise defaults to redirect_uris[0], which sends a
  // subpath deployment (sites.wisp.place/...) to a different origin where the
  // PKCE state does not exist.
  const [redirectUri, setRedirectUri] = useState(null);
  const [canonicalUrl, setCanonicalUrl] = useState(null);
  const [notice, setNotice] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [deployment, setDeployment] = useState(null);
  const initializing = useRef(false);

  useEffect(() => {
    const initialise = async () => {
      if (initializing.current || client) return;
      initializing.current = true;
      setLoading(true);
      setError(null);

      try {
        const oauthClient = new BrowserOAuthClient({
          clientMetadata,
          handleResolver: 'https://public.api.bsky.app',
          plcDirectoryUrl: 'https://plc.directory',
        });

        setClient(oauthClient);

        const registered = clientMetadata.redirect_uris || [];
        const here = `${window.location.origin}${appBasename()}/login/callback`;
        setRedirectUri(registered.includes(here) ? here : null);
        setCanonicalUrl(registered.length ? new URL(registered[0]).origin : null);

        // init() both completes a callback and restores a stored session. A
        // stored session that can no longer be refreshed (expired, revoked, or
        // created against other client metadata) must not take the app down
        // with it: the library has already dropped its pointer, so fall back to
        // signed-out and say so.
        try {
          const initResult = await oauthClient.init();
          setSession(initResult?.session ?? null);
        } catch (restoreErr) {
          console.warn('Stored session could not be restored:', restoreErr);
          setSession(null);
          setNotice('Your previous session could not be restored. Sign in again.');
        }
      } catch (err) {
        // Construction can only fail on a build-time metadata problem, never
        // on the network. Surface it with a reset path regardless.
        console.error('Auth initialisation failed:', err);
        setErrorDetail(err?.message || String(err));
        setError(
          'Sign-in is unavailable right now: the OAuth client could not start. Reload to retry; if it keeps failing the deployed build has broken client metadata.',
        );
        setSession(null);
      } finally {
        setLoading(false);
        initializing.current = false;
      }
    };

    initialise();

    checkDeployment().then((info) => {
      if (info?.stale) console.warn('Stale bundle running:', info.running, 'deployed:', info.deployed);
      setDeployment(info);
    });
  }, [client]);

  useEffect(() => {
    if (!session) {
      setAgent(null);
      setMe(null);

      return;
    }

    let cancelled = false;

    try {
      setAgent(new Agent(session));
    } catch (err) {
      console.error('Failed to construct Agent:', err);
      setAgent(null);
    }

    const did = session.did || session.sub;
    getPdsEndpoint(did)
      .then((pds) => {
        if (!cancelled) setMe({ did, pds, handle: session.handle || did });
      })
      .catch((err) => {
        console.error('Failed to resolve PDS for the session:', err);

        if (!cancelled) setMe({ did, pds: null, handle: session.handle || did });
      });

    return () => {
      cancelled = true;
    };
  }, [session]);

  const login = useCallback(
    async (handle, returnUrl = '/') => {
      if (!client) {
        setError('Sign-in is not ready yet. Wait a moment and try again.');

        return;
      }

      if (!redirectUri) {
        setError(
          canonicalUrl
            ? `Sign in from ${canonicalUrl} — this host is not a registered redirect target, so the login would come back to the wrong place.`
            : 'Sign-in is unavailable: the client metadata lists no redirect target for this host.',
        );

        return;
      }

      try {
        await client.signIn(handle.trim().replace(/^@/, ''), {
          state: JSON.stringify({ returnUrl }),
          redirect_uri: redirectUri,
        });
      } catch (err) {
        console.error('Login failed:', err);
        setError(
          err?.message?.includes('cancelled')
            ? 'Sign-in was cancelled.'
            : 'Could not start sign-in for that handle. Check the spelling of your handle and try again.',
        );
      }
    },
    [client, redirectUri, canonicalUrl],
  );

  const logout = useCallback(async () => {
    try {
      if (session) await session.signOut();
    } catch (err) {
      console.error('Sign-out failed:', err);
    }

    setSession(null);
    setAgent(null);
    setMe(null);
  }, [session]);

  return (
    <AuthContext.Provider
      value={{
        client,
        session,
        agent,
        me,
        loading,
        error,
        isAuthenticated: Boolean(session),
        notice,
        errorDetail,
        deployment,
        resetSignIn: resetLocalSignInState,
        redirectUri,
        canonicalUrl,
        login,
        logout,
        clearError: () => setError(null),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === null) throw new Error('useAuth must be used within an AuthProvider');

  return context;
};
